import {
  IPaymentInfo,
  IBillingTransactions,
  IBillingAccount,
  DirectDebitInfo,
  PaymentStatus,
  IBillingDocument,
} from '@dch/billing-business-logic';
import { hasAnyOverdueTransactionWithOngoingDd } from '@dch/billing-business-logic/lib/services/transactionService';

export interface IPayButtonVisibility {
  isBalancePayButtonVisible: boolean;
  isBalanceOverduePayButtonVisible: boolean;
  isTransactionPayButtonVisible: boolean;
}

export function isPayButtonVisible(
  paymentInfoResult: IPaymentInfo,
  billingTransactions: IBillingTransactions,
  billingAccount: IBillingAccount
): IPayButtonVisibility {
  let payButtonVisibility = {
    isBalancePayButtonVisible: false,
    isBalanceOverduePayButtonVisible: false,
    isTransactionPayButtonVisible: false,
  };

  const billingDocuments =
    billingTransactions.openTransactions.billingDocuments;

  const hasEmptyBillingDocuments = billingDocuments.length === 0;

  const hasAtLeastOneOngoingPaymentTransaction =
    hasAtLeastOneBillingDocumentPaymentStatus(
      billingDocuments,
      PaymentStatus.ONGOING_PAYMENT
    );

  const hastAtLeastOneBillingDocumentWithInstalmentStatus =
    hasAtLeastOneBillingDocumentPaymentStatus(
      billingDocuments,
      PaymentStatus.INSTALMENT
    );

  const hastAtLeastOneBillingDocumentWithDirectDebitPaymentInfoOngoing =
    billingDocuments.some(
      (billingDocument) =>
        billingDocument.paymentInfo?.directDebitInfo === DirectDebitInfo.ONGOING
    );

  const hasManualPaymentMethod = paymentInfoResult.paymentMethod === 'OK';

  const hasAtLeastOneOverdueTransactionWithOngoingDD =
    hasAnyOverdueTransactionWithOngoingDd(billingTransactions);

  if (
    hasEmptyBillingDocuments ||
    hasAtLeastOneOngoingPaymentTransaction ||
    hastAtLeastOneBillingDocumentWithInstalmentStatus ||
    (hastAtLeastOneBillingDocumentWithDirectDebitPaymentInfoOngoing &&
      hasManualPaymentMethod) ||
    (hasAtLeastOneOverdueTransactionWithOngoingDD && !hasManualPaymentMethod) ||
    Number(billingAccount.balance) <= 0
  ) {
    return payButtonVisibility;
  } else {
    return getPayButtonVisibilityInSpecialCases(
      billingDocuments,
      billingAccount,
      hasManualPaymentMethod,
      payButtonVisibility
    );
  }
}

function getPayButtonVisibilityInSpecialCases(
  billingDocuments: IBillingDocument[],
  billingAccount: IBillingAccount,
  hasManualPaymentMethod: boolean,
  payButtonVisibility: IPayButtonVisibility
): IPayButtonVisibility {
  let payButtonVisibilityForManualOrDD = payButtonVisibility;

  const hasOverdueBalanceHigherThanZero =
    Number(billingAccount.overdueBalance) > 0;

  if (hasManualPaymentMethod) {
    payButtonVisibilityForManualOrDD.isBalancePayButtonVisible = true;
    payButtonVisibilityForManualOrDD.isBalanceOverduePayButtonVisible =
      hasOverdueBalanceHigherThanZero &&
      billingAccount.balance !== billingAccount.overdueBalance;
  } else {
    return setPayButtonVisibilityForDDuser(
      billingDocuments,
      payButtonVisibilityForManualOrDD
    );
  }
  return payButtonVisibilityForManualOrDD;
}

function setPayButtonVisibilityForDDuser(
  billingDocuments: IBillingDocument[],
  payButtonVisibilityForManualOrDD: IPayButtonVisibility
) {
  const hasOnlyOneBillingDocument = billingDocuments.length === 1;
  const hasAllBillingDocumentsOpenPaymentStatus =
    areAllBillingDocumentsPaymentStatus(billingDocuments, PaymentStatus.OPEN);

  if (hasOnlyOneBillingDocument) {
    if (
      billingDocuments[0].paymentInfo?.directDebitInfo ===
        DirectDebitInfo.MANUAL ||
      isTransactionRefusedDirectDebit(billingDocuments[0])
    ) {
      payButtonVisibilityForManualOrDD.isBalancePayButtonVisible = true;
    }
  } else {
    if (hasAllBillingDocumentsOpenPaymentStatus) {
      return setPayButtonVisibilityForDDuserMultipleDocumentsOpenOnly(
        billingDocuments,
        payButtonVisibilityForManualOrDD
      );
    } else {
      return setPayButtonVisibilityForDDuserMultipleDocumentsOpenOrOverdue(
        billingDocuments,
        payButtonVisibilityForManualOrDD
      );
    }
  }

  return payButtonVisibilityForManualOrDD;
}

function setPayButtonVisibilityForDDuserMultipleDocumentsOpenOnly(
  billingDocuments: IBillingDocument[],
  payButtonVisibilityForManualOrDDMultipleOpenOnly: IPayButtonVisibility
) {
  const hasAllBillingDocumentsDirectDebitPaymentInfoManual =
    checkIfAllDocumentsHaveDDInfoManual(billingDocuments);
  const hasAtLeastOneBillingDocumentDirectDebitPaymentInfoManual =
    checkIfOneDocumentHasDDInfoManual(billingDocuments);

  if (hasAllBillingDocumentsDirectDebitPaymentInfoManual) {
    payButtonVisibilityForManualOrDDMultipleOpenOnly.isBalancePayButtonVisible =
      true;
  } else {
    if (hasAtLeastOneBillingDocumentDirectDebitPaymentInfoManual) {
      payButtonVisibilityForManualOrDDMultipleOpenOnly.isTransactionPayButtonVisible =
        true;
    }
  }
  return payButtonVisibilityForManualOrDDMultipleOpenOnly;
}

function setPayButtonVisibilityForDDuserMultipleDocumentsOpenOrOverdue(
  billingDocuments: IBillingDocument[],
  payButtonVisibilityForManualOrDDMultipleOpenOrOverdue: IPayButtonVisibility
) {
  const hasAllBillingDocumentsOverduePaymentStatus =
    areAllBillingDocumentsPaymentStatus(
      billingDocuments,
      PaymentStatus.OVERDUE
    );

  if (hasAllBillingDocumentsOverduePaymentStatus) {
    payButtonVisibilityForManualOrDDMultipleOpenOrOverdue.isBalancePayButtonVisible =
      areAllBillingDocumentsManualOrRefusedDDOrBothCombined(billingDocuments);
  } else {
    if (
      areAllBillingDocumentsManualOrRefusedDDOrBothCombined(billingDocuments)
    ) {
      payButtonVisibilityForManualOrDDMultipleOpenOrOverdue.isBalancePayButtonVisible =
        true;
      payButtonVisibilityForManualOrDDMultipleOpenOrOverdue.isBalanceOverduePayButtonVisible =
        true;
    } else {
      payButtonVisibilityForManualOrDDMultipleOpenOrOverdue.isBalanceOverduePayButtonVisible =
        areAllOverdueBillingDocumentsManualOrRefusedDDOrBothCombined(
          billingDocuments
        );
    }
  }

  return payButtonVisibilityForManualOrDDMultipleOpenOrOverdue;
}

function isTransactionRefusedDirectDebit(billingDocument: IBillingDocument) {
  return (
    billingDocument.paymentInfo?.directDebitInfo ===
      DirectDebitInfo.HARDREFUSED ||
    billingDocument.paymentInfo?.directDebitInfo === DirectDebitInfo.SOFTREFUSED
  );
}

export function areAllBillingDocumentsPaymentStatus(
  billingDocuments: IBillingDocument[],
  paymentStatus: PaymentStatus
) {
  return billingDocuments.every(
    (document) => document.paymentStatus === paymentStatus
  );
}

export function hasAtLeastOneBillingDocumentPaymentStatus(
  billingDocuments: IBillingDocument[],
  paymentStatus: PaymentStatus
) {
  return billingDocuments.some(
    (document) => document.paymentStatus === paymentStatus
  );
}

function checkIfOneDocumentHasDDInfoManual(
  billingDocuments: IBillingDocument[]
) {
  return billingDocuments.some(
    (billingDocument) =>
      billingDocument.paymentInfo?.directDebitInfo === DirectDebitInfo.MANUAL
  );
}

function checkIfAllDocumentsHaveDDInfoManual(
  billingDocuments: IBillingDocument[]
) {
  return billingDocuments.every(
    (billingDocument) =>
      billingDocument.paymentInfo?.directDebitInfo === DirectDebitInfo.MANUAL
  );
}

function areAllBillingDocumentsManualOrRefusedDDOrBothCombined(
  billingDocuments: IBillingDocument[]
) {
  if (billingDocuments.length === 0) {
    return false;
  }

  const hasAllDocumentsDirectDebitPaymentInfoRefusedDD = billingDocuments.every(
    (billingDocument) => isTransactionRefusedDirectDebit(billingDocument)
  );
  const hasAtLeastOneDocumentDirectDebitPaymentInfoRefusedDD =
    billingDocuments.some((billingDocument) =>
      isTransactionRefusedDirectDebit(billingDocument)
    );
  const hasAllDocumentsDirectDebitPaymentInfoManual =
    checkIfAllDocumentsHaveDDInfoManual(billingDocuments);

  const hasAtLeastOneDocumentDirectDebitPaymentInfoManual =
    checkIfOneDocumentHasDDInfoManual(billingDocuments);

  return (
    hasAllDocumentsDirectDebitPaymentInfoRefusedDD ||
    hasAllDocumentsDirectDebitPaymentInfoManual ||
    (hasAtLeastOneDocumentDirectDebitPaymentInfoRefusedDD &&
      hasAtLeastOneDocumentDirectDebitPaymentInfoManual)
  );
}

function areAllOverdueBillingDocumentsManualOrRefusedDDOrBothCombined(
  billingDocuments: IBillingDocument[]
) {
  const overdueDocuments = billingDocuments.filter(
    (document) => document.paymentStatus === PaymentStatus.OVERDUE
  );
  return areAllBillingDocumentsManualOrRefusedDDOrBothCombined(
    overdueDocuments
  );
}
